import { useCallback, useMemo } from "react"
import { useLocation, useSearchParams } from "react-router-dom"

import {
  type LeadMissingFields,
  resolveLeadMissingFields
} from "./lead-missing-fields.ts"

const LEAD_MISSING_PARAM = "missing"
const LEAD_NEXT_STATUS_PARAM = "next_status"

/**
 * Set when the params were rewritten by a rejected retry rather than followed
 * from a link, so the marks re-derive without the arrival effect moving the
 * user off the tab they are editing.
 */
const LEAD_MISSING_IN_PLACE = "leadMissingUpdatedInPlace"

interface LeadMissingFieldsState {
  missing: LeadMissingFields
  /** The raw param, so an effect can run once per distinct deep link. */
  missingParam: string | null
  pendingStatus: number | null
  /** True while the current params came from a retry, not from a new link. */
  updatedInPlace: boolean
  applyRejectedStage: (status: number | null, fields: string[]) => void
  clearPendingStage: () => void
}

const toStatus = (param: string | null): number | null => {
  if (!param) return null
  const status = Number(param)
  return Number.isFinite(status) ? status : null
}

/**
 * The marks a rejected transition asks for, read straight from the deep link.
 * Deriving them from the URL rather than from state is what makes them outlive a
 * refetch: a lead reloaded mid-visit cannot re-mark a field the user just
 * answered, and cannot drag them back to the first missing field's tab. A retry
 * that is rejected again writes its answer back to the same two params, so the
 * marks, the badges and the banner all follow the server's latest word.
 */
function useLeadMissingFields(): LeadMissingFieldsState {
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()

  const missingParam = searchParams.get(LEAD_MISSING_PARAM)
  const pendingStatus = toStatus(searchParams.get(LEAD_NEXT_STATUS_PARAM))
  const locationState = location.state as Record<string, unknown> | null
  const updatedInPlace = !!locationState?.[LEAD_MISSING_IN_PLACE]

  const missing = useMemo(
    () => resolveLeadMissingFields(missingParam),
    [missingParam]
  )

  const applyRejectedStage = useCallback(
    (status: number | null, fields: string[]) => {
      setSearchParams(
        (prev) => {
          if (status == null) prev.delete(LEAD_NEXT_STATUS_PARAM)
          else prev.set(LEAD_NEXT_STATUS_PARAM, String(status))

          if (fields.length) prev.set(LEAD_MISSING_PARAM, fields.join(","))
          else prev.delete(LEAD_MISSING_PARAM)

          return prev
        },
        {
          replace: true,
          state: { ...locationState, [LEAD_MISSING_IN_PLACE]: true }
        }
      )
    },
    [setSearchParams, locationState]
  )

  const clearPendingStage = useCallback(() => {
    setSearchParams(
      (prev) => {
        prev.delete(LEAD_MISSING_PARAM)
        prev.delete(LEAD_NEXT_STATUS_PARAM)
        return prev
      },
      { replace: true }
    )
  }, [setSearchParams])

  return {
    missing,
    missingParam,
    pendingStatus,
    updatedInPlace,
    applyRejectedStage,
    clearPendingStage
  }
}

export {
  type LeadMissingFieldsState,
  LEAD_MISSING_IN_PLACE,
  LEAD_MISSING_PARAM,
  LEAD_NEXT_STATUS_PARAM,
  useLeadMissingFields
}
